import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import Nav from "./Nav";
import Footer from "./Footer";
import "./home.css";
import "./Sales.css";
import StandardPageMain from "../Components/StandardCardMain";
import DraftQuoteLines from "../Components/DraftQuoteLines";
import ErrorMessage from "../Components/ErrorPopup";
import SuccessMessage from "../Components/SuccessPopup";
import useFetch from "../Components/UseFetch";
import { parseApiError } from "../HelperFx/common.js";
import verifyToken from "../HelperFx/verifyJwt.js";
import {
  companyDefault,
  confirmDraftQuote as CDQ,
  draftQuotes as DQ,
  reopenDraftQuote,
} from "../HelperFx/datalinks";

const REACT_UNAME = process.env.REACT_APP_API_USERNAME;
const REACT_PWD = process.env.REACT_APP_API_PASSWORD;
const REACT_DATA_URL = process.env.REACT_APP_API_DATA_URL;
const REACT_ACTION_URL = process.env.REACT_APP_ACTION_PI_DATA_URL;

const headerPatchFields = new Set([
  "email",
  "clientPhoneNo",
  "clientJobTitle",
  "reasonForQuotation",
  "postingDescription",
  "additionalDescription",
  "mou",
  "expectedReceiptDate",
  "expectedConfirmationDate",
  "sellToContact",
  "sellToAddress",
]);

const toDateInput = (value) => {
  if (!value) return "";
  const text = String(value);
  if (text.includes("0001-01-01") || text.startsWith("0001-")) return "";
  return text.slice(0, 10);
};

const DRAFT_QUOTE_TABLE_ID = 50227;

const mapHeader = (record, portalUserName = "") => ({
  ...record,
  documentNo: record.no,
  documentType: record.documentType || "Quote",
  status: record.confirm ? "Confirmed" : "Open",
  expectedReceiptDate: toDateInput(record.expectedReceiptDate),
  expectedConfirmationDate: toDateInput(record.expectedConfirmationDate),
  documentDate: toDateInput(record.documentDate),
  statusText: record.confirm ? "Processing Confirmed" : "Open",
  portalUser: record.portalUser || portalUserName || "",
});

const DraftOrder = () => {
  const location = useLocation();
  const token = btoa(`${REACT_UNAME}:${REACT_PWD}`);
  const jwttoken = sessionStorage.getItem("authToken");
  const { userName } = verifyToken(jwttoken) || {};
  const docNo = location.state?.createdNumber || location.state?.number || "";
  const [header, setHeader] = useState({});
  const [errorOpen, setErrorOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successOpen, setSuccessOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  const headerUrl = docNo
    ? `${REACT_DATA_URL}${DQ}?${companyDefault}&$filter=no eq '${docNo}'`
    : null;
  const { data: headerResponse, error: headerFetchError } = useFetch(
    headerUrl || `${REACT_DATA_URL}${DQ}?${companyDefault}&$top=0`,
    token,
  );

  useEffect(() => {
    const record = headerResponse?.value?.[0];
    if (record) {
      setHeader(mapHeader(record, userName));
    }
  }, [headerResponse, userName]);

  useEffect(() => {
    const record = headerResponse?.value?.[0];
    if (!record?.systemId || record.portalUser || !userName) return;

    const fillPortalUser = async () => {
      try {
        const response = await axios.patch(
          `${REACT_DATA_URL}${DQ}(${record.systemId})?${companyDefault}`,
          { portalUser: userName },
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Basic ${token}`,
              "If-Match": "*",
            },
          },
        );
        setHeader(mapHeader(response.data, userName));
      } catch (err) {
        setHeader((current) => ({
          ...current,
          portalUser: current.portalUser || userName,
        }));
      }
    };

    fillPortalUser();
  }, [headerResponse, token, userName]);

  useEffect(() => {
    if (headerFetchError) {
      setErrorMessage(
        typeof headerFetchError === "string"
          ? headerFetchError
          : "Unable to load the quotation header.",
      );
      setErrorOpen(true);
    }
  }, [headerFetchError]);

  const showError = (message) => {
    setErrorMessage(message);
    setErrorOpen(true);
  };

  const handlePageBlur = async (field, value) => {
    if (!headerPatchFields.has(field)) return;
    if (!header.systemId) {
      showError("The quotation header has not loaded yet.");
      return;
    }

    try {
      const response = await axios.patch(
        `${REACT_DATA_URL}${DQ}(${header.systemId})?${companyDefault}`,
        { [field]: value },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Basic ${token}`,
            "If-Match": "*",
          },
        },
      );
      setHeader(mapHeader(response.data, userName));
    } catch (err) {
      showError(parseApiError(err));
    }
  };

  const refreshHeader = useCallback(async () => {
    if (!headerUrl) return;
    const refreshed = await axios.get(headerUrl, {
      headers: { Authorization: `Basic ${token}` },
    });
    const record = refreshed.data?.value?.[0];
    if (record) {
      setHeader(mapHeader(record, userName));
    }
  }, [headerUrl, token, userName]);

  const runAction = useCallback(
    async (actionName, body, successText) => {
      try {
        const response = await axios.post(
          `${REACT_ACTION_URL}${actionName}?${companyDefault}`,
          body,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Basic ${token}`,
            },
          },
        );
        if (response.status >= 400) {
          throw new Error(response.statusText);
        }
        setSuccessMessage(successText);
        setSuccessOpen(true);
        await refreshHeader();
      } catch (err) {
        showError(parseApiError(err));
      }
    },
    [refreshHeader, token],
  );

  const confirmed = Boolean(header.confirm);
  const fields = [
    { name: "no", label: "Quote No.", type: "text", disabled: true },
    { name: "sellToCustomerNo", label: "Customer No.", type: "text", disabled: true },
    { name: "portalUser", label: "Portal User", type: "text", disabled: true },
    { name: "email", label: "Contact Email", type: "email" },
    { name: "clientPhoneNo", label: "Phone Number", type: "text" },
    { name: "clientJobTitle", label: "Job Title", type: "text" },
    {
      name: "reasonForQuotation",
      label: "Reason For Quotation",
      type: "select",
      options: [
        { value: "Planning", label: "Planning" },
        { value: "Budgeting", label: "Budgeting" },
        { value: "Price Confirmation", label: "Price Confirmation" },
        { value: "Purchasing To Choose 1", label: "Purchasing" },
      ],
    },
    { name: "postingDescription", label: "Quote Description", type: "text" },
    { name: "additionalDescription", label: "Notice", type: "text" },
    { name: "mou", label: "Memorandum Of Understanding", type: "text" },
    { name: "expectedConfirmationDate", label: "Expected Confirmation Date", type: "date" },
    { name: "expectedReceiptDate", label: "Expected Receipt Date", type: "date" },
    { name: "sellToContact", label: "Contact", type: "text" },
    { name: "sellToAddress", label: "Address", type: "text" },
    { name: "statusText", label: "Status", type: "text", disabled: true },
    { name: "documentDate", label: "Document Date", type: "date", disabled: true },
    { name: "amount", label: "Amount", type: "decimal", format: "amount", disabled: true },
  ];

  const actions = useMemo(
    () => [
      confirmed
        ? {
            name: "Re-Open",
            fx: () =>
              runAction(
                reopenDraftQuote,
                { docNo },
                "Request for quotation re-opened.",
              ),
          }
        : {
            name: "Confirm Processing",
            fx: () =>
              runAction(
                CDQ,
                { docNo },
                "Request for quotation confirmed.",
              ),
          },
    ],
    [confirmed, docNo, runAction],
  );

  if (!docNo) {
    return (
      <div className="Home">
        <Nav />
        <div className="dash-main">
          <div className="squote">
            <div className="firsttab px-4">Request For Quotation</div>
            <p style={{ padding: 20 }}>No quotation number was provided.</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="Home">
      <Nav />
      <div className="dash-main">
        <div className="squote">
          <div className="firsttab px-4">Request For Quotation {docNo}</div>
          <div className="listTable">
            <StandardPageMain
              title={`Request For Quotation - ${docNo}`}
              fields={fields}
              initialData={header}
              actions={actions}
              redirectTo="Draftorders"
              onBlur={handlePageBlur}
              tableId={DRAFT_QUOTE_TABLE_ID}
              PreventEdit={confirmed}
              authToken={token}
              attachmentApi={`${REACT_DATA_URL}documentAttachments`}
              onAttachmentError={showError}
            />
            <DraftQuoteLines
              docNo={docNo}
              token={token}
              allowEdit={!confirmed}
              onLinesChanged={refreshHeader}
            />
            <ErrorMessage
              isOpen={errorOpen}
              toggle={() => setErrorOpen(false)}
              caption="Error"
              message={errorMessage}
            />
            <SuccessMessage
              isOpen={successOpen}
              toggle={() => setSuccessOpen(false)}
              caption="Success"
              message={successMessage}
            />
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default DraftOrder;
